/**
 * Reactions: four Tibbs stickers (REDESIGN.md 4.3). Tapped from the smiley
 * button; one pops over the sender's tile for two seconds. No text on screen;
 * screen readers hear the sticker's name.
 */
import { useEffect, useRef, useState } from 'react';
import { EMOTES } from '../../../worker/protocol';
import type { Emote } from '../../../worker/protocol';
import { STICKER_LABELS } from '../../brand';
import { Tibbs } from '../Art';
import type { TibbsPose } from '../Art';
import { IconSmile } from '../Icons';

const POSE: Record<Emote, TibbsPose> = { 'well-played': 'applaud', oof: 'retract', smug: 'smug', fore: 'fore' };

export function Sticker({ emote, className = '' }: { emote: Emote; className?: string }) {
  return (
    <span className={`sticker-pop ${className}`} role="status" aria-label={STICKER_LABELS[emote]}>
      <Tibbs pose={POSE[emote]} size={52} />
    </span>
  );
}

/** The reaction button and its four-sticker tray. Rate-limited to 3 per 5 seconds. */
export function StickerButton({ onSend }: { onSend: (e: Emote) => void }) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState<number[]>([]);
  const wrap = useRef<HTMLDivElement>(null);
  const limited = sent.filter((t) => Date.now() - t < 5000).length >= 3;
  useEffect(() => {
    if (!open) return;
    const off = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('pointerdown', off, true);
    return () => window.removeEventListener('pointerdown', off, true);
  }, [open]);
  return (
    <div className="sticker-wrap" ref={wrap}>
      <button type="button" className="icon-btn sticker-toggle" aria-expanded={open} aria-label="Send a reaction" onClick={() => setOpen((o) => !o)}>
        <IconSmile />
      </button>
      {open && (
        <div className="sticker-tray" role="menu">
          {EMOTES.map((e) => (
            <button
              key={e}
              type="button"
              role="menuitem"
              className="sticker-btn"
              disabled={limited}
              aria-label={STICKER_LABELS[e]}
              title={STICKER_LABELS[e]}
              onClick={() => {
                setSent((s) => [...s.filter((t) => Date.now() - t < 5000), Date.now()]);
                onSend(e);
                setOpen(false);
              }}
            >
              <Tibbs pose={POSE[e]} size={48} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
